import React, { useState, useRef, useEffect } from 'react';

interface DropdownOption {
    id: string;
    label: string;
    icon?: React.ReactNode;
    selected?: boolean;
}

interface DropdownProps {
    trigger: React.ReactNode;
    options: DropdownOption[];
    onSelect: (optionId: string) => void;
    multiSelect?: boolean;
    align?: 'left' | 'right';
    minWidth?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
    trigger,
    options,
    onSelect,
    multiSelect = false,
    align = 'left',
    minWidth = '160px'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleOptionClick = (optionId: string) => {
        onSelect(optionId);
        if (!multiSelect) {
            setIsOpen(false);
        }
    };

    const handleTriggerClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent card drag from interfering
        e.preventDefault();
        setIsOpen(!isOpen);
    };

    return (
        <div 
            ref={dropdownRef} 
            style={{ 
                position: 'relative',
                outline: isOpen ? '2px solid var(--color-primary-300)' : 'none',
                outlineOffset: '2px',
                borderRadius: 'var(--radius-md)',
                transition: 'outline 200ms ease-in-out'
            }} 
            className="dropdown-container"
        >
            <div 
                onClick={handleTriggerClick}
                onMouseDown={(e) => e.stopPropagation()} // Prevent card's onMouseDown from triggering
                className="dropdown-trigger"
                style={{ cursor: 'pointer' }}
            >
                {trigger}
            </div>

            {isOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 99998
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(false);
                        }}
                        onMouseDown={(e) => e.stopPropagation()} // Prevent card drag
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + var(--spacing-sm))',
                            [align]: 0,
                            minWidth,
                            backgroundColor: 'var(--color-surface)',
                            border: '1px solid var(--color-border-light)',
                            borderRadius: 'var(--radius-lg)',
                            boxShadow: 'var(--shadow-xl)',
                            padding: 'var(--spacing-sm)',
                            zIndex: 99999999
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling to card
                        onMouseDown={(e) => e.stopPropagation()} // Prevent card drag
                    >
                        {options.map((option) => (
                            <button
                                key={option.id}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOptionClick(option.id);
                                }}
                                onMouseDown={(e) => e.stopPropagation()} // Prevent card drag
                                style={{
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    borderRadius: 'var(--radius-md)',
                                    backgroundColor: option.selected
                                        ? 'var(--color-primary-50)'
                                        : 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--color-text-primary)',
                                    fontSize: '0.875rem',
                                    transition: 'background-color 200ms ease-in-out',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 'var(--spacing-sm)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = option.selected
                                        ? 'var(--color-primary-100)'
                                        : 'var(--color-gray-100)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = option.selected
                                        ? 'var(--color-primary-50)'
                                        : 'transparent';
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                    {option.icon}
                                    <span>{option.label}</span>
                                </div>
                                {option.selected && (
                                    <span style={{ color: 'var(--color-primary-600)' }}>✓</span>
                                )}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Dropdown;
